import { createClient, SupabaseClient, GoTrueClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Variáveis de ambiente do Supabase não definidas');
}

/**
 * Serviço Singleton para gerenciar a instância do cliente Supabase
 */
class SupabaseService {
  private static instance: SupabaseService;
  private client: SupabaseClient;
  private authClient: GoTrueClient;
  private static isInitialized = false;

  private constructor() {
    if (SupabaseService.isInitialized) {
      throw new Error('SupabaseService já foi inicializado. Use getInstance()');
    }

    this.client = createClient(supabaseUrl as string, supabaseAnonKey as string, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true
      },
      realtime: {
        params: {
          eventsPerSecond: 2
        }
      }
    });

    this.authClient = this.client.auth;

    console.log('[Supabase] Cliente inicializado');
    SupabaseService.isInitialized = true;
    Object.freeze(this); // Previne modificações na instância
  }

  public static getInstance(): SupabaseService {
    if (!SupabaseService.instance) {
      console.log('[Supabase] Criando nova instância do serviço');
      SupabaseService.instance = new SupabaseService();
      Object.freeze(SupabaseService.instance); // Garante imutabilidade
    } else {
      console.log('[Supabase] Reusando instância existente');
      // Garante que estamos usando a mesma instância do GoTrueClient
      SupabaseService.instance.client.auth = SupabaseService.instance.authClient;
    }
    
    console.log('[Supabase] Retornando instância existente');
    return SupabaseService.instance;
  }

  public getClient(): SupabaseClient {
    return this.client;
  }

  // Método para acessar o cliente de autenticação
  public getAuthClient(): GoTrueClient {
    return this.authClient;
  }

  // Função para tentar uma operação com retry
  public async withRetry<T>(
    operation: () => Promise<{ data: T | null; error: any }>,
    maxRetries = 3,
    delayMs = 1000
  ): Promise<{ data: T | null; error: any }> {
    let attempts = 0;
    
    while (attempts < maxRetries) {
      try {
        const result = await operation();
        
        // Se não houver erro, retorna o resultado
        if (!result.error) {
          return result;
        }

        // Se o erro for de autenticação ou conexão, tenta reconectar
        if (
          result.error.message?.includes('JWT') ||
          result.error.message?.includes('connection') ||
          result.error.message?.includes('timeout')
        ) {
          await new Promise(resolve => setTimeout(resolve, delayMs * (attempts + 1)));
          attempts++;
          continue;
        }

        // Se for outro tipo de erro, retorna imediatamente
        return result;
      } catch (error) {
        console.error(`[Supabase][${new Date().toISOString()}] Erro na operação:`, error);
        attempts++;
        
        if (attempts === maxRetries) {
          return { data: null, error };
        }
        
        await new Promise(resolve => setTimeout(resolve, delayMs * attempts));
      }
    }

    return { data: null, error: new Error('Máximo de tentativas excedido') };
  }
}

// Exporta uma instância única e imutável do serviço
export const supabaseService = SupabaseService.getInstance();

// Exporta o cliente para manter compatibilidade com código existente
export const supabase = supabaseService.getClient();

// Exporta as funções auxiliares para manter compatibilidade com código existente
export const withRetry = <T>(
  operation: () => Promise<{ data: T | null; error: any }>,
  maxRetries?: number,
  delayMs?: number
) => supabaseService.withRetry(operation, maxRetries, delayMs);